/**
 * Formatting utilities for show details display
 */

import { readableForeground } from '@/theme/contrast';
import { STATUS_COLORS, POSITION_COLORS, POSITION_LABELS } from './constants';

/**
 * Format time string without seconds (HH:MM format)
 */
export const formatTime = (timeString: string | null | undefined): string => {
  if (!timeString) return 'TBD';
  // Remove seconds if present (HH:MM:SS -> HH:MM)
  return timeString.substring(0, 5);
};

/**
 * Format currency value with USD locale
 */
export const formatCurrency = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

/**
 * Get MUI color name for hold status
 * Delegates to STATUS_COLORS from constants.ts for single source of truth
 */
export const getStatusColor = (
  status: string,
):
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning' => {
  // Use STATUS_COLORS from constants; pending: 'warning' (not 'info')
  return (STATUS_COLORS[status] || 'info') as
    | 'default'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning';
};

/**
 * Get hex color for hold position badge
 */
export const getPositionColor = (position: number): string => {
  return POSITION_COLORS[position] || '#CCCCCC';
};

/**
 * Get ordinal text for any hold position, including teen exceptions.
 */
export const getPositionText = (position: number): string => {
  if (position < 1) return 'No reservation';
  if (POSITION_LABELS[position]) return POSITION_LABELS[position];
  const lastTwoDigits = position % 100;
  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) return `${position}th`;
  const suffixes: Record<number, string> = { 1: 'st', 2: 'nd', 3: 'rd' };
  return `${position}${suffixes[position % 10] || 'th'}`;
};

/** Paired styles shared by every hold-position badge. */
export const getPositionBadgeStyles = (position: number) => ({
  backgroundColor: getPositionColor(position),
  color: readableForeground(getPositionColor(position)),
});
